import { useEffect, useRef, useState } from "react";
import { FiClock } from "react-icons/fi";

interface ChallengeCountdownProps {
	/** Seconds remaining when the challenge was issued (server cache TTL). */
	expiresInSeconds: number;
	/**
	 * Fired once when the countdown reaches zero. The parent uses this to push the
	 * user back to the login screen with the "took too long" message — mirroring
	 * the server's own expiry so the FE never leaves a dead challenge on screen.
	 */
	onExpire: () => void;
}

const formatRemaining = (totalSeconds: number): string => {
	const safe = Math.max(0, totalSeconds);
	const minutes = Math.floor(safe / 60);
	const seconds = safe % 60;
	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

/**
 * Yellow, live countdown for the pending 2FA challenge. Anchored to an absolute
 * deadline (issued-at + TTL) rather than a plain decrementing counter, so a
 * backgrounded/throttled tab still reflects real elapsed time and fires
 * {@link ChallengeCountdownProps.onExpire} at the correct moment.
 */
export const ChallengeCountdown = ({
	expiresInSeconds,
	onExpire,
}: ChallengeCountdownProps) => {
	const [remaining, setRemaining] = useState(() => Math.max(0, Math.floor(expiresInSeconds)));

	// Keep the latest onExpire without re-arming the interval each render.
	const onExpireRef = useRef(onExpire);
	onExpireRef.current = onExpire;

	useEffect(() => {
		const deadline = Date.now() + Math.max(0, expiresInSeconds) * 1000;
		let fired = false;

		const tick = () => {
			const secondsLeft = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
			setRemaining(secondsLeft);
			if (secondsLeft <= 0 && !fired) {
				fired = true;
				clearInterval(intervalId);
				onExpireRef.current();
			}
		};

		tick();
		const intervalId = setInterval(tick, 1000);
		return () => clearInterval(intervalId);
	}, [expiresInSeconds]);

	return (
		<div
			className="mb-5 flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800"
			data-testid="challenge-countdown"
		>
			<FiClock className="h-4 w-4 shrink-0" aria-hidden="true" />
			<span>
				This login attempt expires in{" "}
				<span className="font-semibold tabular-nums" aria-live="off">
					{formatRemaining(remaining)}
				</span>
				.
			</span>
		</div>
	);
};
